import React, { useMemo } from "react"
import { Trans, useTranslation } from "react-i18next"

import { ExternalDimensioningConfigurationMismatch } from "shared/types/ExternalDimensioning"
import { ProductConfiguratorStepType } from "shared/types/ProductConfiguration"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { Hint } from "shared/ui/hint/Hint"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { useProductConfiguratorContext } from "../../model/ProductConfiguratorContext"
import { getExternalDimensioningMismatches } from "../../model/getExternalDimensioningMismatches"
import "./styles/_product-configurator-external-dimensioning-error-hint.scss"

interface ProductConfiguratorExternalDimensioningErrorHintProps {
  onAdjustDimensioning: () => void
}

function MismatchList({
  mismatches
}: {
  mismatches: ExternalDimensioningConfigurationMismatch[]
  showPosition?: boolean
}) {
  const { t } = useTranslation()
  return mismatches.map((mismatch, index) => {
    const fallback = t(
      "Product.Configuration.DimensioningErrorHintDimensioningAttributeLabelFallback"
    )
    const label =
      mismatch.label ||
      (mismatch.position ? `${fallback} ${mismatch.position}` : fallback)
    return (
      <Trans
        key={JSON.stringify(mismatch)}
        i18nKey="Product.Configuration.DimensioningErrorHintDimensioningAttribute"
        values={{
          label,
          input: mismatch.input,
          output: mismatch.cap,
          suffix: index !== mismatches.length - 1 ? ", " : ""
        }}
        components={{ strong: <span className="font-bold" /> }}
      />
    )
  })
}

export const ProductConfiguratorExternalDimensioningErrorHint = React.memo(
  ({
    onAdjustDimensioning
  }: ProductConfiguratorExternalDimensioningErrorHintProps) => {
    const { t } = useTranslation()
    const { activeStep, externalDimensioningConfigurationMismatchError } =
      useProductConfiguratorContext()

    const mismatches = useMemo<
      ExternalDimensioningConfigurationMismatch[]
    >(() => {
      return getExternalDimensioningMismatches(
        externalDimensioningConfigurationMismatchError
      )
    }, [externalDimensioningConfigurationMismatchError])

    if (mismatches.length === 0) {
      return null
    }

    const isDimensioningStep =
      activeStep === ProductConfiguratorStepType.DIMENSIONING

    if (isDimensioningStep) {
      return (
        <div className="product-configurator__error-message">
          <Hint.Root
            variant="warning"
            className="hint--grid product-configurator__dimensioning-hint"
          >
            <Hint.Icon name={IconName.INFO} />
            <Hint.Title>
              {t(
                "Product.Configuration.DimensioningErrorHintDimensioningTitle"
              )}
            </Hint.Title>
            <Hint.Content>
              <Hint.Text>
                {t(
                  "Product.Configuration.DimensioningErrorHintDimensioningIntro"
                )}
              </Hint.Text>
              <Hint.Text>
                <MismatchList mismatches={mismatches} />
              </Hint.Text>
            </Hint.Content>
          </Hint.Root>
        </div>
      )
    }

    return (
      <div className="product-configurator__error-message">
        <Hint.Root
          variant="warning"
          className="hint--grid product-configurator__dimensioning-hint"
        >
          <Hint.Icon name={IconName.INFO} />
          <Hint.Title>
            {t("Product.Configuration.ConfigurationAutoAdjusted")}
          </Hint.Title>
          <Hint.Content>
            <Hint.Text>
              {t("Product.Configuration.DimensioningErrorHintConfigIntro")}
            </Hint.Text>
            <Hint.Text>
              <MismatchList mismatches={mismatches} showPosition />
            </Hint.Text>
            <Hint.Text>
              {t("Product.Configuration.DimensioningErrorHintConfigFollowUp")}
            </Hint.Text>

            <Hint.Section className="mt-2">
              <Hint.SectionHeader>
                <strong>
                  {t(
                    "Product.Configuration.DimensioningErrorHintRecommendationLabel"
                  )}
                </strong>
              </Hint.SectionHeader>
              <Hint.Text>
                {t(
                  "Product.Configuration.DimensioningErrorHintRecommendationText"
                )}
              </Hint.Text>
            </Hint.Section>
          </Hint.Content>
          <div className="hint__actions">
            <Button
              variant={ButtonVariant.SECONDARY}
              onClick={onAdjustDimensioning}
            >
              {t("Product.Configuration.AdjustDimensioning")}
            </Button>
          </div>
        </Hint.Root>
      </div>
    )
  }
)
